import { useState } from 'react';
import { useDrag } from './DragContext';
import ContextMenu from './ContextMenu';
import { useTheme } from '../../hooks/useTheme';

export default function ItemNode({ item, collectionId, slotPosition, isCompact, isUltraCompact, isMiniCompact, slotId, isActive = false, onAddFilter, onItemClick, onUnlink }) {
  const { startDrag, endDrag, isDragging, handleDrop, hoveredSlot, setHovered, draggedItem } = useDrag();
  const { currentThemeType, themeTypes } = useTheme();
  const [contextMenu, setContextMenu] = useState(null);

  const isLightTheme = currentThemeType === themeTypes.LIGHT;

  const isHovered = isDragging && draggedItem?.id !== item.id && hoveredSlot === slotId;

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;

    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;

    let dragStarted = false;

    const handleMouseMove = (e) => {
      const distance = Math.sqrt(
        Math.pow(e.clientX - startX, 2) + Math.pow(e.clientY - startY, 2)
      );

      if (!dragStarted && distance > 3) {
        dragStarted = true;
        startDrag(item, collectionId, slotPosition, isCompact, e.clientX, e.clientY);
      }
    };

    const handleMouseUp = (e) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (dragStarted) {
        endDrag();
      } else {
        onItemClick?.(item);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleMouseEnter = () => {
    if (isDragging && draggedItem?.id !== item.id) {
      setHovered(slotId);
    }
  };

  const handleMouseLeave = () => {
    if (isDragging && hoveredSlot === slotId) {
      setHovered(null);
    }
  };

  const handleMouseUp = (e) => {
    // Handle drop when another item is being dragged over this one
    if (isDragging && draggedItem?.id !== item.id && hoveredSlot === slotId) {
      e.stopPropagation();
      handleDrop(collectionId, slotPosition);
    }
  };

  const contextMenuOptions = [
    {
      label: 'Copy Title',
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>,
      onClick: () => {
        navigator.clipboard.writeText(item.title);
        setContextMenu(null);
      },
    },
    {
      label: 'Edit',
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>,
      onClick: () => console.log('Edit item:', item),
    },
    { divider: true },
    {
      label: 'Filter Linked To',
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>,
      onClick: () => onAddFilter?.({ type: 'linked-to', itemId: item.item_id, itemTitle: item.title }),
    },
    {
      label: 'Filter Unlinked To',
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>,
      onClick: () => onAddFilter?.({ type: 'unlinked-to', itemId: item.item_id, itemTitle: item.title }),
    },
    { divider: true },
    {
      label: 'Unlink',
      icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>,
      onClick: () => {
        setContextMenu(null);
        onUnlink?.(item.id, collectionId);
      },
      danger: true,
    },
  ];

  return (
    <>
    <div
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onContextMenu={handleContextMenu}
      className={`${isLightTheme ? 'bg-gradient-to-br from-rose-50 to-pink-100' : 'bg-gradient-to-br from-gray-800 to-purple-950'} border-2 rounded-xl shadow-xl ${isDragging ? '' : 'transition-all duration-200 hover:shadow-2xl hover:scale-105'} flex flex-col justify-between cursor-grab select-none ${isMiniCompact ? 'w-24 h-16 p-1.5' : isUltraCompact ? 'w-32 h-20 p-2' : isCompact ? 'w-40 h-24 p-3' : 'w-52 h-32 p-4 sm:p-5'} ${isDragging ? 'opacity-50' : ''} ${isHovered ? 'border-green-400 shadow-green-400/50 scale-110' : isActive ? 'border-yellow-400 shadow-yellow-400/50 scale-105' : isLightTheme ? 'border-rose-300 hover:border-rose-400' : 'border-gray-600 hover:border-purple-500'}`} style={{ color: 'var(--color-text-on-accent)' }}>
            <div className="flex items-start space-x-2">
        <svg className={`${isLightTheme ? 'text-rose-500' : 'text-purple-400'} flex-shrink-0 mt-0.5 ${isMiniCompact ? 'w-2.5 h-2.5' : isUltraCompact ? 'w-3 h-3' : isCompact ? 'w-3 h-3 sm:w-4 sm:h-4' : 'w-4 h-4 sm:w-5 sm:h-5'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        {item.title === "Generating title..." ? (
          <div className="flex items-center gap-1.5 w-full">
            <div className={`flex-1 ${isLightTheme ? 'bg-rose-200' : 'bg-gray-700'} rounded animate-pulse ${isUltraCompact ? 'h-2' : isCompact ? 'h-3' : 'h-3.5'}`}></div>
            <svg className={`animate-spin ${isLightTheme ? 'text-rose-500' : 'text-purple-400'} ${isUltraCompact ? 'w-2 h-2' : 'w-3 h-3'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : (
          <p className={`${isLightTheme ? 'text-gray-800' : 'text-white'} font-semibold line-clamp-2 ${isUltraCompact ? 'text-[10px]' : isCompact ? 'text-xs' : 'text-sm'}`} title={item.title}>
            {item.title || <span className={`${isLightTheme ? 'text-rose-300' : 'text-gray-400'} italic`}>No title</span>}
          </p>
        )}
      </div>
      <div className={`border-t ${isLightTheme ? 'border-rose-200' : 'border-gray-600'} pt-1.5 ${isMiniCompact ? 'mt-0.5 pt-0.5' : isUltraCompact ? 'mt-0.5' : isCompact ? 'mt-1' : 'mt-2'}`}>
        <p className={`${isLightTheme ? 'text-rose-400' : 'text-gray-400'} uppercase tracking-wider ${isMiniCompact ? 'text-[6px]' : isUltraCompact ? 'text-[8px]' : 'text-[10px]'}`}>Linked Item</p>
      </div>
    </div>

    {contextMenu && (
      <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        options={contextMenuOptions}
        onClose={() => setContextMenu(null)}
      />
    )}
    </>
  );
}
